import { Meteor } from 'meteor/meteor';
import { Mongo } from 'meteor/mongo';
import { DeclareMethod } from '/imports/methods';
import { Doc, FileMeta, GetMethodResponse, ListMethodArg, ListMethodResponse, ModelClass, References } from '/imports/types';
import { validateString } from '/imports/validators';
import { strings } from '/imports/strings';

export interface Message extends Doc {
  conversationId: string;
  senderUserId: string;
  senderAccountId?: string;
  senderName: string;
  senderOrgName: string;
  senderLabel?: string;   // who they are to the project: "Luminia", or an external stakeholder's company — shown beside the name
  content: string;
  file?: FileMeta;        // an attachment, uploaded through the normal chunked path and token-verified on send
  mentionUserIds?: string[];  // people named with @ in the text; they are told at once, outside the email quiet window
  editedAt?: Date;
  deletedAt?: Date;       // retracted by its sender: the bubble stays as a placeholder, the text is gone
  readByOrgIds?: string[];
}

/** How long a sender may edit or retract their own message. */
export const MESSAGE_EDIT_WINDOW_MS = 10 * 60 * 1000;

/** One person in a conversation, as the feed shows them: for the header, for @mentions and for "seen by". */
export interface ConversationParticipant {
  userId?: string;
  accountId?: string;
  name: string;
  label?: string;
}
export type Model = Message;

export const Messages = new Mongo.Collection<Message>('messages');

type Refs = References<Model>

export const modelClass: ModelClass<Model, Refs> = {
  collection: Messages,
  editableFields: ['conversationId', 'senderUserId', 'senderAccountId', 'senderName', 'senderOrgName', 'senderLabel', 'content', 'file', 'mentionUserIds'],
  fieldLimits: {
    'content': 50000, 'conversationId': 64, 'senderAccountId': 64, 'senderLabel': 256, 'senderName': 256, 'senderOrgName': 256, 'senderUserId': 64,
  },
  listMethod: DeclareMethod<ListMethodArg<Message>, ListMethodResponse<Message, Refs>>('listMessages'),
  getMethod: DeclareMethod<string, GetMethodResponse<Message, Refs>>('getMessage'),
  createMethod: DeclareMethod<Partial<Message>, string>('createMessage'),
  updateMethod: DeclareMethod<Partial<Message>, string>('updateMessage'),
  deleteMethod: DeclareMethod<string>('deleteMessage'),
  pathPrefix: '/messages',
  asString: (document) => document.content,
  modelName: strings.Message(),
  pluralName: strings.Messages(),
  /**
   * Builds a Mongo query matching messages by id or content substring.
   * @param search - free-text search term
   * @returns Mongo selector ({} when search is empty)
   */
  searchQuery: (search?: string) => {
    search = search?.trim();
    if (!search?.length) return {};
    return {
      $or: [
        { _id: search },
        { content: new RegExp(search, 'gi') },
      ]
    };
  },
  /**
   * Validates a message's required fields.
   * @param message - partial message to validate
   * @returns map of field name to validation errors
   */
  validate: async (message: Partial<Message>) => {
    const errors: { [key: string]: string[] } = {};
    validateString(errors, message, 'conversationId', { required: true });
    validateString(errors, message, 'senderUserId', { required: true });
    validateString(errors, message, 'senderName', { required: true });
    validateString(errors, message, 'senderOrgName', { required: true });
    validateString(errors, message, 'content', { required: true });
    return errors;
  },
};

export interface MessageReceipt extends Doc {
  conversationId: string;
  messageId: string;
  userId: string;
  snippet?: string;
  senderName?: string;
  text?: string;          // the message's opening, for the email and the bell — the snippet kept the org prefix
  mention?: boolean;      // the reader was @mentioned: told immediately, not batched
  readAt?: Date;
  notifiedAt?: Date;
}
export const MessageReceipts = new Mongo.Collection<MessageReceipt>('messageReceipt');

export const markConversationMessagesRead = DeclareMethod<string>('markConversationMessagesRead');
/** Sender-only, within MESSAGE_EDIT_WINDOW_MS: replaces the text and stamps editedAt. */
export const editMessage = DeclareMethod<{ _id: string, content: string }, void>('editMessage');
/** Sender-only, within MESSAGE_EDIT_WINDOW_MS: clears the text and stamps deletedAt. */
export const retractMessage = DeclareMethod<string, void>('retractMessage');
/** Everyone in a conversation — project stakeholders with accounts plus anyone named on it. */
export const conversationParticipants = DeclareMethod<string, ConversationParticipant[]>('conversationParticipants');

Meteor.isServer && Meteor.startup(async () => {
  await Messages.createIndexAsync({ conversationId: 1 });
  await MessageReceipts.createIndexAsync({ userId: 1 });
  await MessageReceipts.createIndexAsync({ conversationId: 1 });
  await MessageReceipts.createIndexAsync({ readAt: 1 }, { sparse: true });
  await MessageReceipts.createIndexAsync({ notified: 1 }, { sparse: true });
});